import type {
  Allocation,
  BalanceResult,
  Category,
  Concept,
  ConceptVersion,
  Holiday,
  Household,
  HouseholdSettings,
  Occurrence,
  CancelledOccurrence,
  PeriodOverride,
  Person,
  Receipt,
  Receivable,
  ScheduleException,
  Transaction,
  VersionTimelineEntry,
  LeisureVerdict,
  WeekendPeriod,
  ClassificationRule,
  Suggestion,
  Cell,
  PeriodInfo,
} from '@balance/domain';

export class ApiError extends Error {
  constructor(public status: number, message: string, public issues: Array<{ path: string; message: string }> = []) {
    super(message);
  }
}

let todayOverride: string | null = null;
export function setTodayOverride(value: string | null): void {
  todayOverride = value;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (todayOverride) headers['x-balance-today'] = todayOverride;
  const res = await fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const issues = (data?.issues ?? []) as Array<{ path: string; message: string }>;
    const detail = issues.length ? `: ${issues.map((i) => `${i.path} ${i.message}`).join('; ')}` : '';
    throw new ApiError(res.status, `${data?.error ?? res.statusText}${detail}`, issues);
  }
  return data as T;
}

export interface Catalog {
  household: Household;
  categories: Category[];
  concepts: Concept[];
  versions: ConceptVersion[];
  exceptions: ScheduleException[];
  overrides: PeriodOverride[];
  persons: Person[];
  holidays: Holiday[];
}

export interface Movements {
  transactions: Transaction[];
  allocations: Allocation[];
  receivables: Receivable[];
  receipts: Receipt[];
}

export interface ConceptDetail {
  concept: Concept;
  versions: ConceptVersion[];
  exceptions: ScheduleException[];
  overrides: PeriodOverride[];
  timeline: VersionTimelineEntry[];
  occurrences: Occurrence[];
  cancelled: CancelledOccurrence[];
  financing: Occurrence[];
  audit: Array<{ id: number; actor: string; at: string; entity: string; entityId: string; action: string; before: unknown; after: unknown }>;
}

export interface VersionPayload {
  validFrom: string;
  state: 'active' | 'inactive';
  frequency: ConceptVersion['frequency'];
  amountCents: number | null;
  schedule: ConceptVersion['schedule'];
  principalCents?: number | null;
  note?: string | null;
}

export interface ConceptPayload {
  name: string;
  categoryId: string | null;
  direction: Concept['direction'];
  behavior: Concept['behavior'];
  personId?: string | null;
  meter?: boolean;
  notes?: string | null;
  sortOrder?: number;
}

export interface AllocationPayload {
  id?: string;
  conceptId: string;
  period: string;
  occurrenceKeys?: string[];
  subPeriod?: string | null;
  amountCents: number;
  recoverableCents?: number;
  isMainMonthly?: boolean;
  note?: string | null;
  dueOn?: string | null;
  decidedBy?: string;
}

export interface TransactionPayload {
  date: string;
  time?: string | null;
  description: string;
  amountCents: number;
  direction: 'out' | 'in';
  source: Transaction['source'];
  personId?: string | null;
  isInternalTransfer?: boolean;
  notes?: string | null;
}

export interface RulePayload {
  name: string;
  pattern: string;
  mode: ClassificationRule['mode'];
  conceptId?: string | null;
  leisureConceptId?: string | null;
  askPeriod?: boolean;
  priority?: number;
  validFrom?: string;
  validTo?: string | null;
  note?: string | null;
}

export interface MailSource {
  id: string;
  householdId: string;
  label: string;
  kind: 'gmail' | 'file';
  address: string | null;
  config: { query?: string; envPrefix?: string };
  checkpoint: { lastReceivedAt?: string; lastMessageId?: string };
  enabled: boolean;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  gmailConfigured: boolean;
}

export interface MailMessageRecord {
  id: string;
  sourceId: string;
  messageId: string;
  receivedAt: string;
  subject: string;
  sender: string;
  parsed: { kind: string; amountCents: number | null; direction: 'out' | 'in' | null; merchant: string | null; date: string | null; time: string | null; missing: string[]; dateFromReceipt: boolean } | null;
  parseStatus: 'parsed' | 'unparsed';
  transactionId: string | null;
  candidateTransactionId: string | null;
  candidateMessageId: string | null;
  reviewStatus: 'none' | 'candidate' | 'linked' | 'created' | 'dismissed';
}

export interface SyncRun {
  id: string;
  sourceId: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'partial' | 'failed';
  fetched: number;
  imported: number;
  duplicates: number;
  candidates: number;
  unparsed: number;
  errors: Array<{ messageId: string; error: string }>;
  note: string | null;
}

export interface SyncState {
  sources: MailSource[];
  runs: SyncRun[];
  candidates: MailMessageRecord[];
  unparsed: MailMessageRecord[];
}

export interface RawMailMessage {
  messageId: string;
  threadId?: string | null;
  receivedAt: string;
  subject: string;
  from: string;
  body: string;
}

export type AuthMode = 'open' | 'shared' | 'users';

export interface AuthStatus {
  mode: AuthMode;
  enabled: boolean;
  authenticated: boolean;
  user: { username: string; displayName: string } | null;
}

export interface AppUser {
  id: string;
  username: string;
  displayName: string;
  personId: string | null;
  createdAt: string;
}

export interface Checkpoint {
  userKey: string;
  lastPage: string | null;
  lastSeenAt: string | null;
  snapshot: unknown;
}

export interface LeisureInfo {
  period: string;
  weekends: WeekendPeriod[];
  floatingHolidays: Holiday[];
  leisureDays: string[];
  rule: 'start' | 'end';
}

export const api = {
  authStatus: () => request<AuthStatus>('GET', '/api/auth/status'),
  login: (password: string, username?: string) => request<{ ok: boolean }>('POST', '/api/auth/login', { password, ...(username ? { username } : {}) }),
  users: () => request<AppUser[]>('GET', '/api/users'),
  createUser: (body: { username: string; displayName: string; password: string; personId?: string | null }) => request<AppUser>('POST', '/api/users', body),
  changeMyPassword: (current: string, next: string) => request<{ ok: boolean }>('PUT', '/api/users/me/password', { current, next }),
  deleteUser: (username: string) => request<void>('DELETE', `/api/users/${encodeURIComponent(username)}`),
  logout: () => request<{ ok: boolean }>('POST', '/api/auth/logout', {}),
  household: () => request<{ household: Household | null; persons: Person[]; defaults: HouseholdSettings }>('GET', '/api/household'),
  createHousehold: (body: { name: string; openingPeriod: string; openingBalanceCents: number; persons: string[] }) => request<Household>('POST', '/api/household', body),
  updateHousehold: (body: { name?: string; openingPeriod?: string; openingBalanceCents?: number; settings?: Partial<HouseholdSettings> }) => request<Household>('PATCH', '/api/household', body),
  balance: (from?: string, to?: string) => request<BalanceResult>('GET', `/api/balance${from || to ? `?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}` : ''}`),
  catalog: () => request<Catalog>('GET', '/api/catalog'),
  movements: () => request<Movements>('GET', '/api/transactions'),
  concept: (id: string) => request<ConceptDetail>('GET', `/api/concepts/${id}`),
  createConcept: (body: ConceptPayload & { version: VersionPayload }) => request<{ concept: Concept; version: ConceptVersion }>('POST', '/api/concepts', body),
  updateConcept: (id: string, body: Partial<ConceptPayload>) => request<Concept>('PATCH', `/api/concepts/${id}`, body),
  deleteConcept: (id: string) => request<void>('DELETE', `/api/concepts/${id}`),
  addVersion: (id: string, body: VersionPayload) => request<ConceptVersion>('POST', `/api/concepts/${id}/versions`, body),
  deleteVersion: (id: string, versionId: string) => request<void>('DELETE', `/api/concepts/${id}/versions/${versionId}`),
  addException: (id: string, body: { kind: 'cancel' | 'move' | 'add'; originalDate: string; newDate?: string | null; amountCents?: number | null; note?: string | null }) => request<ScheduleException>('POST', `/api/concepts/${id}/exceptions`, body),
  deleteException: (id: string) => request<void>('DELETE', `/api/exceptions/${id}`),
  addOverride: (id: string, body: { period: string; amountCents: number; note?: string | null }) => request<PeriodOverride>('POST', `/api/concepts/${id}/overrides`, body),
  deleteOverride: (id: string) => request<void>('DELETE', `/api/overrides/${id}`),
  preview: (body: { concept: Partial<ConceptPayload> & { id?: string }; versions: VersionPayload[]; exceptions?: Array<{ kind: 'cancel' | 'move' | 'add'; originalDate: string; newDate?: string | null; amountCents?: number | null }> }) =>
    request<{ periods: PeriodInfo[]; cells: Cell[]; timeline: VersionTimelineEntry[] }>('POST', '/api/concepts/preview', body),
  createCategory: (body: { name: string; sortOrder?: number }) => request<Category>('POST', '/api/categories', body),
  updateCategory: (id: string, body: { name?: string; sortOrder?: number }) => request<Category>('PATCH', `/api/categories/${id}`, body),
  createTransaction: (body: { transaction: TransactionPayload; allocations?: AllocationPayload[] }) => request<{ transaction: Transaction; allocations: Allocation[] }>('POST', '/api/transactions', body),
  updateTransaction: (id: string, body: Partial<TransactionPayload>) => request<Transaction>('PATCH', `/api/transactions/${id}`, body),
  deleteTransaction: (id: string) => request<void>('DELETE', `/api/transactions/${id}`),
  replaceAllocations: (id: string, allocations: AllocationPayload[]) => request<{ transaction: Transaction; allocations: Allocation[] }>('PUT', `/api/transactions/${id}/allocations`, { allocations }),
  addReceipt: (receivableId: string, body: { amountCents: number; receivedOn: string; transactionId?: string | null; note?: string | null }) => request<Receipt>('POST', `/api/receivables/${receivableId}/receipts`, body),
  closeReceivable: (receivableId: string, closedOn?: string) => request<Receivable>('POST', `/api/receivables/${receivableId}/close`, closedOn ? { closedOn } : {}),
  updateReceivable: (receivableId: string, dueOn: string) => request<Receivable>('PATCH', `/api/receivables/${receivableId}`, { dueOn }),
  runCloseOverdue: () => request<{ closed: number; ids: string[] }>('POST', '/api/jobs/close-overdue-receivables'),
  rules: () => request<ClassificationRule[]>('GET', '/api/rules'),
  createRule: (body: RulePayload) => request<ClassificationRule>('POST', '/api/rules', body),
  updateRule: (id: string, body: Partial<RulePayload>) => request<ClassificationRule>('PATCH', `/api/rules/${id}`, body),
  deleteRule: (id: string) => request<void>('DELETE', `/api/rules/${id}`),
  suggestions: () => request<Suggestion[]>('GET', '/api/suggestions'),
  sync: () => request<SyncState>('GET', '/api/sync'),
  createSource: (body: { label: string; kind: 'gmail' | 'file'; address?: string | null; config?: { query?: string; envPrefix?: string } }) => request<MailSource>('POST', '/api/sync/sources', body),
  deleteSource: (id: string) => request<void>('DELETE', `/api/sync/sources/${id}`),
  importMessages: (sourceId: string, messages: RawMailMessage[], note?: string) => request<{ run: SyncRun; created: string[] }>('POST', `/api/sync/sources/${sourceId}/import`, { messages, note }),
  runSync: (sourceId: string, body: { from?: string | null; to?: string | null } = {}) => request<{ run: SyncRun; created: string[] }>('POST', `/api/sync/sources/${sourceId}/run`, body),
  resolveMessage: (id: string, action: 'link' | 'create' | 'dismiss', transactionId?: string | null) => request<MailMessageRecord>('POST', `/api/sync/messages/${id}/resolve`, { action, transactionId }),
  holidays: () => request<Holiday[]>('GET', '/api/holidays'),
  addHoliday: (body: { date: string; name: string; scope: string; source?: string | null }) => request<Holiday>('POST', '/api/holidays', body),
  deleteHoliday: (id: string) => request<void>('DELETE', `/api/holidays/${id}`),
  leisure: (period: string) => request<LeisureInfo>('GET', `/api/leisure/${period}`),
  classify: (date: string, time?: string | null) => request<LeisureVerdict>('GET', `/api/leisure/classify?${new URLSearchParams({ date, ...(time ? { time } : {}) })}`),
  audit: (params: { entity?: string; entityId?: string; limit?: number } = {}) => request<ConceptDetail['audit']>('GET', `/api/audit?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])))}`),
  checkpoint: (userKey: string) => request<Checkpoint>('GET', `/api/checkpoint/${userKey}`),
  saveCheckpoint: (userKey: string, body: { lastPage?: string | null; lastSeenAt?: string | null; snapshot?: unknown }) => request<Checkpoint>('PUT', `/api/checkpoint/${userKey}`, body),
};
